/**
 * NativeFilterPanel - Wrapper for the original Data-View filter-bar component
 *
 * Embeds the Lit `filter-bar` web component from @blocksuite/affine/data-view
 * to provide the exact same UX as the Database filter UI.
 */

import { useI18n } from '@affine/i18n';
import type { FilterGroup, Variable } from '@blocksuite/affine/data-view';
import { t } from '@blocksuite/affine/data-view';
import { LockIcon } from '@blocksuite/icons/rc';
import { type ReadonlySignal, signal } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import {
  type ColumnInfo,
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../services/database-discovery';
import { CollapsibleSection } from './collapsible-section';
import * as styles from './native-filter-panel.css';
import { SectionEmpty, SectionNotice } from './section-layout';

interface SystemFilterInfo {
  /** Column name for display */
  columnName: string;
  /** Value that is being filtered out */
  filterValue: string;
}

interface NativeFilterPanelProps {
  /** Source table block ID to get columns from */
  sourceTableId: string | null;
  /** Current filter group (defaults to empty group if undefined) */
  filterGroup?: FilterGroup;
  /** Callback when filter changes */
  onChange: (filterGroup: FilterGroup) => void;
  /** System filter info (from status tracking) */
  systemFilter?: SystemFilterInfo | null;
  /** Callback when (i) icon is clicked - shows section coachmark */
  onInfoClick?: () => void;
  /** sl-m379: Optional label suffix for multi-table filters (e.g. table name) */
  label?: string;
  /** sl-m379: Render without CollapsibleSection wrapper (for embedding in a shared section) */
  noWrapper?: boolean;
}

/**
 * Convert a column type to a Data-View TypeInstance using the t type factory.
 */
function columnTypeToTypeInstance(columnType: string) {
  switch (columnType) {
    case 'number':
    case 'progress':
      return t.number.instance();
    case 'checkbox':
      return t.boolean.instance();
    case 'date':
      return t.date.instance();
    case 'select':
      return t.tag.instance();
    case 'multi-select':
      return t.tag.instance();
    case 'title':
    case 'rich-text':
      // Title column uses richText type (see title/define.ts)
      return t.richText.instance();
    case 'url':
      return t.url.instance();
    case 'text':
    default:
      return t.string.instance();
  }
}

/**
 * Convert database columns to Data-View Variable format
 */
function columnsToVariables(columns: ColumnInfo[]): Variable[] {
  return columns.map(col => ({
    id: col.id,
    name: col.name,
    type: columnTypeToTypeInstance(col.type),
    propertyType: col.type,
  }));
}

/**
 * Default empty FilterGroup to prevent crashes when filterGroup is undefined
 */
const DEFAULT_FILTER_GROUP: FilterGroup = {
  type: 'group',
  op: 'and',
  conditions: [],
};

/**
 * Minimal mock for dataViewLogic - only the required parts for FilterBar
 */
function createMinimalDataViewLogic() {
  return {
    root: {
      config: {
        dataSource: {
          // eslint-disable-next-line rxjs/finnish
          readonly$: signal(false),
        },
      },
    },
    eventTrace: () => {
      // No-op for analytics
    },
  };
}

export const NativeFilterPanel: FC<NativeFilterPanelProps> = ({
  sourceTableId,
  filterGroup: filterGroupProp,
  onChange,
  systemFilter,
  onInfoClick,
  label,
  noWrapper,
}) => {
  const t = useI18n();
  // Ensure filterGroup is never undefined to prevent crashes in FilterBar
  const filterGroup = filterGroupProp ?? DEFAULT_FILTER_GROUP;

  const discoveryService = useService(DatabaseDiscoveryService);
  const databases = useLiveData(discoveryService.databases$);

  // Find columns for the selected source table
  const sourceTable = sourceTableId
    ? databases.find((db: DatabaseInfo) => db.blockId === sourceTableId)
    : null;
  const columns = useMemo(
    () => sourceTable?.columns ?? [],
    [sourceTable?.columns]
  );

  // Count active filters (including system filter from status tracking)
  const userFilterCount = filterGroup?.conditions?.length ?? 0;
  const systemFilterCount = systemFilter ? 1 : 0;
  const filterCount = userFilterCount + systemFilterCount;

  // Check for invalid filters (column IDs that don't exist in current source table)
  // SingleFilter structure: { type: 'filter', left: { type: 'ref', name: columnId }, ... }
  const columnIds = useMemo(() => new Set(columns.map(c => c.id)), [columns]);
  const invalidFilterCount = useMemo(() => {
    if (!filterGroup?.conditions) return 0;
    return filterGroup.conditions.filter(
      (condition: { type?: string; left?: { name?: string } }) => {
        if (condition.type !== 'filter') return false;
        const columnId = condition.left?.name;
        return columnId && !columnIds.has(columnId);
      }
    ).length;
  }, [filterGroup?.conditions, columnIds]);

  // Clear invalid filters
  const clearInvalidFilters = useCallback(() => {
    if (!filterGroup?.conditions) return;
    const validConditions = filterGroup.conditions.filter(
      (condition: { type?: string; left?: { name?: string } }) => {
        if (condition.type !== 'filter') return true; // Keep groups
        const columnId = condition.left?.name;
        return !columnId || columnIds.has(columnId);
      }
    );
    onChange({
      ...filterGroup,
      conditions: validConditions,
    });
  }, [filterGroup, columnIds, onChange]);

  // Create stable signals that persist across renders
  const varsSignal = useMemo(
    () => signal(columnsToVariables(columns)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Only create once
  );
  const filterGroupSignal = useMemo(
    () => signal(filterGroup),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Only create once
  );
  const dataViewLogic = useMemo(() => createMinimalDataViewLogic(), []);

  // Stable onChange handler
  const handleChange = useCallback(
    (newFilterGroup: FilterGroup) => {
      onChange(newFilterGroup);
    },
    [onChange]
  );

  // Update vars signal when columns change
  useEffect(() => {
    varsSignal.value = columnsToVariables(columns);
  }, [columns, varsSignal]);

  // Update filterGroup signal when prop changes
  useEffect(() => {
    filterGroupSignal.value = filterGroup;
  }, [filterGroup, filterGroupSignal]);

  // Badge for filter count
  const badge =
    filterCount > 0
      ? { label: String(filterCount), status: 'active' as const }
      : undefined;

  // Show placeholder if no source table selected
  if (!sourceTableId) {
    if (noWrapper) return null;
    return (
      <CollapsibleSection
        title={
          label
            ? `${t['com.streamlain.streamlain.input.filters']()} — ${label}`
            : t['com.streamlain.streamlain.input.filters']()
        }
        subtitle={t['com.streamlain.streamlain.input.filters-subtitle']()}
        onInfoClick={onInfoClick}
        defaultCollapsed
      >
        <SectionEmpty>
          {t['com.streamlain.streamlain.input.select-source-first']?.() ??
            'Select a source table first'}
        </SectionEmpty>
      </CollapsibleSection>
    );
  }

  if (columns.length === 0) {
    if (noWrapper) return null;
    return (
      <CollapsibleSection
        title={
          label
            ? `${t['com.streamlain.streamlain.input.filters']()} — ${label}`
            : t['com.streamlain.streamlain.input.filters']()
        }
        subtitle={t['com.streamlain.streamlain.input.filters-subtitle']()}
        onInfoClick={onInfoClick}
        defaultCollapsed
      >
        <SectionEmpty>
          {t['com.streamlain.streamlain.input.no-columns-in-source']()}
        </SectionEmpty>
      </CollapsibleSection>
    );
  }

  // sl-m379: Inner content (filter bar + system filter + warnings)
  const filterContent = (
    <>
      {/* System Filter (from Status Tracking) */}
      {systemFilter && (
        <SectionNotice
          variant="system"
          icon={<LockIcon />}
          action={
            <span className={styles.systemFilterBadge}>
              {t['com.streamlain.streamlain.common.auto']()}
            </span>
          }
        >
          <span>{`${systemFilter.columnName} ≠ "${systemFilter.filterValue}"`}</span>
        </SectionNotice>
      )}

      {/* Invalid filter warning */}
      {invalidFilterCount > 0 && (
        <SectionNotice
          variant="warning"
          action={
            <button
              onClick={clearInvalidFilters}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: cssVarV2('others/warnLabel/yellow'),
                color: cssVarV2('text/primary'),
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {t['com.streamlain.streamlain.common.remove']()}
            </button>
          }
        >
          <span>
            {invalidFilterCount} invalid filter
            {invalidFilterCount > 1 ? 's' : ''} (column not found)
          </span>
        </SectionNotice>
      )}

      {/* Filter Bar */}
      <div
        data-coachmark-target="filter-bar-container"
        ref={el => {
          if (!el) return;

          // Add a unique class to scope CSS to only this filter-bar
          el.classList.add('streamlain-filter-container');

          // Add scoped CSS to make filter-bar display vertically and style the add button
          const styleId = 'streamlain-filter-bar-styles';
          if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .streamlain-filter-container filter-bar {
                  flex-direction: column !important;
                  align-items: stretch !important;
                }
                /* Style the add filter button to match our addButton style */
                .streamlain-filter-container .filter-bar-add-filter {
                  display: flex !important;
                  align-items: center !important;
                  gap: 6px !important;
                  padding: 8px 12px !important;
                  font-size: 13px !important;
                  color: var(--streamlain-v2-text-link) !important;
                  background-color: transparent !important;
                  border: 1px dashed var(--streamlain-v2-layer-insideBorder-border) !important;
                  border-radius: 8px !important;
                  cursor: pointer !important;
                  transition: all 0.15s ease !important;
                  white-space: nowrap !important;
                }
                .streamlain-filter-container .filter-bar-add-filter:hover {
                  background-color: var(--streamlain-v2-layer-background-hoverOverlay) !important;
                  border-color: var(--streamlain-v2-text-link) !important;
                }
              `;
            document.head.append(style);
          }

          // Create filter-bar element if not already created
          let filterBar = el.querySelector('filter-bar') as HTMLElement | null;
          if (!filterBar) {
            filterBar = document.createElement('filter-bar');
            el.append(filterBar);
          }

          // Set properties on the Lit element
          (
            filterBar as unknown as {
              filterGroup: ReadonlySignal<FilterGroup>;
            }
          ).filterGroup = filterGroupSignal;
          (filterBar as unknown as { vars: ReadonlySignal<Variable[]> }).vars =
            varsSignal;
          (
            filterBar as unknown as { onChange: (fg: FilterGroup) => void }
          ).onChange = handleChange;
          (filterBar as unknown as { dataViewLogic: unknown }).dataViewLogic =
            dataViewLogic;
        }}
      />
    </>
  );

  // sl-m379: noWrapper mode — return bare content with optional label
  if (noWrapper) {
    return label ? (
      <div style={{ marginTop: '8px' }}>
        <div
          style={{
            fontSize: '11px',
            fontWeight: 600,
            opacity: 0.5,
            marginBottom: '4px',
          }}
        >
          {label}
        </div>
        {filterContent}
      </div>
    ) : (
      filterContent
    );
  }

  return (
    <CollapsibleSection
      title={
        label
          ? `${t['com.streamlain.streamlain.input.filters']()} — ${label}`
          : t['com.streamlain.streamlain.input.filters']()
      }
      subtitle={t['com.streamlain.streamlain.input.filters-subtitle']()}
      badge={badge}
      onInfoClick={onInfoClick}
      defaultCollapsed
    >
      {filterContent}
    </CollapsibleSection>
  );
};
